// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit, Input, HostListener } from '@angular/core';
import { UserService } from 'src/services/user-service';
import { IUserSettings, IUser } from 'src/types/user';
import { D3eService } from 'src/services/d3e-service';
import { combineLatest } from 'rxjs';
import { NotificationService } from 'src/services/notification-service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-user-settings',
    templateUrl: './user-settings.component.html',
    styleUrls: ['./user-settings.component.scss'],
    standalone: false
})
export class UserSettingsComponent implements OnInit {
  settings: IUserSettings = {
    alert_frequency: 'never',
    opt_out: {},
    severity_notify: 0,
    email_notify_language: 'en'
  };
  sensorTypes: {displayValue: string; value: number; description: string; hidden: boolean; }[];
  prefs: {displayValue: string; value: number; email: boolean; in_app: boolean; }[] = [];
  selectAllEmail = false;
  selectAllEmailIndeterminate = false;
  selectAllInApp = false;
  selectAllInAppIndeterminate = false;
  isDirty = false;

  fEmailFreq: string;
  fTNever: string;
  fT5Min: string;
  fTHour: string;
  fTDay: string;
  fAdvanced: string;
  fEventSeverity: string;
  fEInfo: string;
  fEWarning: string;
  fEError: string;
  fCancel: string;
  fSave: string;
  fEmail: string;
  fType: string;
  fInApp: string;
  fEmailTip: string;
  fEmailNoti: string;
  fInAppTip: string;
  fInAppNoti: string;
  fEmailFor: string;
  fInAppFor: string;
  fSaved: string;
  @Input() user: IUser;

  constructor(
    private userService: UserService,
    private d3eService: D3eService,
    public dialog: MatDialog,
    private notification: NotificationService,
    private translate: TranslateService
  ) {
    this.translate.get(['PF_EMAIL', 'PF_TIME_N', 'PF_TIME_5MIN', 'PF_TIME_HOUR', 'PF_TIME_DAY', 'PF_ADVANCED', 'PF_EVENT',
      'SEVERITY_INFO', 'SEVERITY_WARNING', 'SEVERITY_ERROR', 'CANCEL', 'SAVE', 'TYPE', 'PF_BEMAIL', 'PF_BINAPP', 'PF_BEMAIL_TIP',
      'PF_BEMAIL_NOTI', 'PF_BINAPP_TIP', 'PF_BINAPP_NOTI', 'PF_BEMAIL_FOR', 'PF_BINAP_FOR', 'SAVED']).subscribe(vals => {
        this.fEmailFreq = vals['PF_EMAIL'];
        this.fTNever = vals['PF_TIME_N'];
        this.fT5Min = vals['PF_TIME_5MIN'];
        this.fTHour = vals['PF_TIME_HOUR'];
        this.fTDay = vals['PF_TIME_DAY'];
        this.fAdvanced = vals['PF_ADVANCED'];
        this.fEventSeverity = vals['PF_EVENT'];
        this.fEInfo = vals['SEVERITY_INFO'];
        this.fEWarning = vals['SEVERITY_WARNING'];
        this.fEError = vals['SEVERITY_ERROR'];
        this.fCancel = vals['CANCEL'];
        this.fSave = vals['SAVE'];
        this.fType = vals['TYPE'];
        this.fEmail = vals['PF_BEMAIL'];
        this.fInApp = vals['PF_BINAPP'];
        this.fEmailTip = vals['PF_BEMAIL_TIP'];
        this.fEmailNoti = vals['PF_BEMAIL_NOTI'];
        this.fInAppTip = vals['PF_BINAPP_TIP'];
        this.fInAppNoti = vals['PF_BINAPP_NOTI'];
        this.fEmailFor = vals['PF_BEMAIL_FOR'];
        this.fInAppFor = vals['PF_BINAP_FOR'];
        this.fSaved = vals['SAVED'];

    });
  }

  ngOnInit() {
    this.getSettings();    
  }

  getSettings() {
    const settings$ = this.userService.api_get_settings();
    const sensors$ = this.d3eService.getSensorTypes();
    combineLatest([settings$, sensors$]).subscribe( ([settingsResponse, sensorsResponse]) => {
      this.settings = settingsResponse;
      if (!this.settings.opt_out.email) {
        this.settings.opt_out.email = [];
      }
      if (!this.settings.opt_out.in_app) {
        this.settings.opt_out.in_app = [];
      }
      this.sensorTypes = sensorsResponse.sensorTypes.filter(st => st.hidden === false);
      this.resetPrefs();
    });
  }

  resetPrefs() {
    this.prefs = this.sensorTypes.map(st => {
      const email = this.settings.opt_out.email.indexOf(st.value) === -1;
      const in_app = this.settings.opt_out.in_app.indexOf(st.value) === -1;
      return {
        email: email,
        in_app: in_app,
        displayValue: st.description || st.displayValue,
        value: st.value,
      };
    });
    this.resetSelectAllCheckboxes();
    this.isDirty = false;
  }

  resetSelectAllCheckboxes() {
    const emailDeselected = this.prefs.filter(e => !e.email).length;
    const inAppDeselected = this.prefs.filter(e => !e.in_app).length;
    if (emailDeselected === 0) {
      this.selectAllEmail = true;
      this.selectAllEmailIndeterminate = false;
    } else if (emailDeselected === this.sensorTypes.length) {
      this.selectAllEmail = false;
      this.selectAllEmailIndeterminate = false;
    } else {
      this.selectAllEmailIndeterminate = true;
    }
    if (inAppDeselected === 0) {
      this.selectAllInApp = true;
      this.selectAllInAppIndeterminate = false;
    } else if (inAppDeselected === this.sensorTypes.length) {
      this.selectAllInApp = false;
      this.selectAllInAppIndeterminate = false;
    } else {
      this.selectAllInAppIndeterminate = true;
    }
    this.isDirty = true;
  }

  save() {
    const email = this.prefs.filter(pref => {
      return pref.email === false;
    }).map(pref => {
      return pref.value;
    });
    const in_app = this.prefs.filter(pref => {
      return pref.in_app === false;
    }).map(pref => {
      return pref.value;
    });
    this.settings.opt_out.email = email;
    this.settings.opt_out.in_app = in_app;
    this.settings.severity_notify = +this.settings.severity_notify;
    this.userService.api_put_settings(this.settings).subscribe(result => {
      this.notification.showSuccess(this.fSaved);
      this.isDirty = false;
    });
  }

  selectAllEmailChanged(e) {
    this.prefs.forEach(pref => {
      pref.email = e.checked;
    });
    this.isDirty = true;
  }

  selectAllInAppChanged(e) {
    this.prefs.forEach(pref => {
      pref.in_app = e.checked;
    });
    this.isDirty = true;
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  getIsDirty(): boolean {
    return this.isDirty;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadComponent() {
    return !this.isDirty;
  }

  onLanguageChanged(event) {
    this.isDirty = true;
    this.settings.email_notify_language = event;
  }
}
